import { Component, OnDestroy, OnInit } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ActivatedRoute, Router } from '@angular/router';

import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  catchError,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  timeout,
} from 'rxjs';

import { CREDIT_PROPOSAL_TEMPLATE } from '../../data/credit-proposal-template';
import { sectionTitleKey } from '../../data/section-title-keys';

import { DocumentTemplate } from '../../../../app/components/credit/shared/models/document-template.model';
import { Section } from '../../../../app/components/credit/shared/models/section.model';
import { SectionType } from '../../../../app/components/credit/shared/models/section-type.enum';

import { SectionRenderer } from '../../../../app/components/credit/shared/components/section-renderer/section-renderer';

import { DocumentTabs } from '../../document-tabs/document-tabs';

import {
  CraditService,
  SectionSaveResult,
  SectionSaveStatus,
} from '../../../../app/components/credit/shared/services/cradit.service';

import { BackButton } from '../../../../app/components/shared/buttons/back-button';
import { Popup } from '../../../../app/core/shared/components/popup/popup';

import { UnsavedChangesAware } from '../../../../app/core/guards/document-builder-unsaved-changes.guard';
import { DocumentBuilderStateService } from '../../services/document-builder-state.service';
import { SectionSavedDataService } from '../../services/section-saved-data.service';
import { TemplateService } from '../../services/template.service';
import { OnboradingService } from '../../../../app/components/onboarding/shared/service/onboarding.service';

type UnsavedChangesChoice = 'save' | 'discard' | 'stay';

/** Translation key used as the save-timeout fallback message (also used to detect that case). */
const SAVE_NOT_CONFIRMED_MESSAGE = 'DOC_BUILDER.SAVE_NOT_CONFIRMED';

@Component({
  selector: 'app-document-builder-page',
  standalone: true,

  imports: [
    CommonModule,
    FormsModule,
    SectionRenderer,
    DocumentTabs,
    BackButton,
    Popup,
    TranslatePipe,
  ],

  providers: [DocumentBuilderStateService],

  templateUrl: './document-builder-page.html',
  styleUrl: './document-builder-page.css',
})
export class DocumentBuilderPage implements OnInit, OnDestroy, UnsavedChangesAware {
  readonly sectionTitleKey = sectionTitleKey;

  template: DocumentTemplate = CREDIT_PROPOSAL_TEMPLATE;

  enabledSections: Section[] = [];

  isEditMode = true;
  activeSectionId: string | null = null;

  onboardingId = 0;
  crdtPrpslSeq: number | null = null;
  proposalSequenceError = '';
  institutionName = '';

  showUnsavedDialog = false;

  showSubmitCreditProposal = false;
  showSubmitProposalPopup = false;
  submitProposalComment = '';
  isSubmittingCreditProposal = false;

  /** `entyType` of a credit proposal for `/crdt-prpsl/submit-crdt-prpsl` (2 is Leads). */
  private readonly creditProposalEntityType = 12;

  private unsavedDialogResolver: ((choice: UnsavedChangesChoice) => void) | null = null;
  private reportNavigationInFlight = false;

  private readonly subscriptions = new Subscription();

  /** Proposal sequences whose persisted section status should be loaded. */
  private readonly persistedStatusRequests = new Subject<number>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private creditService: CraditService,
    private templateService: TemplateService,
    private toastr: ToastrService,
    private onboardingService: OnboradingService,
    readonly stateService: DocumentBuilderStateService,
    private sectionSavedDataService: SectionSavedDataService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Fresh clone per visit — avoids mutating the shared module-level
    // template constant (which several child sections write into directly).
    this.template = this.templateService.getCreditProposalTemplate();
    this.prepareEnabledSections();
    this.enabledSections.forEach((section) => this.stateService.ensureTracked(section.id));

    this.isEditMode = this.route.snapshot.queryParamMap.get('mode') !== 'view';

    this.onboardingId = this.getRouteSequence();
    this.initializeCreditProposalSequence();

    const institutionNameFromQuery = this.route.snapshot.queryParamMap.get('institutionName');

    if (institutionNameFromQuery) {
      this.institutionName = institutionNameFromQuery;
    } else {
      this.loadInstitutionName();
    }

    // Reads the persisted per-proposal state so a refresh/reopen of a
    // proposal whose plan was already saved keeps the buttons in the right
    // state immediately, without waiting for any API round-trip.
    this.syncSubmitCreditProposalVisibility(this.crdtPrpslSeq);

    // Seeds tab status, saved count and progress from what is already
    // persisted, so they survive a reload/reopen. switchMap drops a stale
    // response if the active proposal changes while a load is in flight.
    const persistedStatusSubscription = this.persistedStatusRequests
      .pipe(
        distinctUntilChanged(),
        switchMap((sequence) =>
          this.sectionSavedDataService.loadSavedSectionIds(sequence, this.enabledSections),
        ),
      )
      .subscribe((savedSectionIds) => this.stateService.applyPersistedSaves(savedSectionIds));

    this.subscriptions.add(persistedStatusSubscription);

    // crdtPrpslSeq$ replays the sequence resolved above, so this also
    // covers the initial load.
    const sequenceSubscription = this.creditService.crdtPrpslSeq$.subscribe((sequence) => {
      const normalizedSequence = Number(sequence ?? 0);

      if (Number.isFinite(normalizedSequence) && normalizedSequence > 0) {
        const sequenceChanged = normalizedSequence !== this.crdtPrpslSeq;

        this.crdtPrpslSeq = normalizedSequence;
        this.persistedStatusRequests.next(normalizedSequence);

        // A different (or brand-new) proposal never inherits another
        // proposal's saved state — re-derive it from what is actually
        // persisted for this specific crdtPrpslSeq.
        if (sequenceChanged) {
          this.syncSubmitCreditProposalVisibility(normalizedSequence);
        }
      }
    });

    this.subscriptions.add(sequenceSubscription);

    // Persists across visits: once the tentative repayment/disbursement plan
    // has been saved for the currently active proposal, "Save & Review
    // Report" stays disabled and "Submit Credit Proposal" stays visible from
    // here on. Events for any other proposal (e.g. a stale emission from a
    // previously open one) are ignored.
    const repaymentPlanCalculatedSubscription =
      this.creditService.repaymentPlanCalculated$.subscribe(({ crdtPrpslSeq, calculated }) => {
        if (crdtPrpslSeq === this.resolveProposalSequence()) {
          this.showSubmitCreditProposal = calculated;
        }
      });

    this.subscriptions.add(repaymentPlanCalculatedSubscription);

    // The Documents tab has no Save: its uploads save themselves, so it is
    // marked saved (green tick, progress) once every required document is in.
    const documentsSubscription = this.creditService.documentsChanged$
      .pipe(
        switchMap(() => {
          const sequence = this.resolveProposalSequence();

          return sequence > 0
            ? this.sectionSavedDataService.loadDocumentsComplete(sequence)
            : of(null);
        }),
      )
      .subscribe((complete) => {
        if (complete !== null) {
          this.stateService.setCompletion('documents', complete);
        }
      });

    this.subscriptions.add(documentsSubscription);

    const queryParamSubscription = this.route.queryParamMap.subscribe((params) => {
      this.syncActiveSectionFromUrl(params.get('section'));
    });

    this.subscriptions.add(queryParamSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get activeSectionIndex(): number {
    if (!this.activeSectionId) {
      return -1;
    }

    return this.enabledSections.findIndex((section) => section.id === this.activeSectionId);
  }

  get currentSection(): Section | null {
    const index = this.activeSectionIndex;
    return index >= 0 ? this.enabledSections[index] : null;
  }

  get previousSection(): Section | null {
    return this.canGoBack ? this.enabledSections[this.activeSectionIndex - 1] ?? null : null;
  }

  get nextSection(): Section | null {
    return this.canGoNext ? this.enabledSections[this.activeSectionIndex + 1] ?? null : null;
  }

  get canGoBack(): boolean {
    return this.enabledSections.length > 0 && this.activeSectionIndex > 0;
  }

  get canGoNext(): boolean {
    return (
      this.enabledSections.length > 0 &&
      this.activeSectionIndex >= 0 &&
      this.activeSectionIndex < this.enabledSections.length - 1
    );
  }

  /**
   * The Documents tab saves each upload as it happens, so it has no Save
   * Draft / Save and Continue — only Continue.
   */
  get isDocumentsSection(): boolean {
    return this.currentSection?.type === SectionType.DOCUMENTS;
  }

  get isLastSection(): boolean {
    return (
      this.enabledSections.length > 0 &&
      this.activeSectionIndex === this.enabledSections.length - 1
    );
  }

  /**
   * Share of enabled sections that have saved data — persisted data seeded on
   * load plus saves made during this visit. Independent of which section is
   * currently open. Labelled "saved", not "completed", since business
   * completeness of a section can't be reliably derived on the frontend.
   */
  get savedProgressPercentage(): number {
    if (this.enabledSections.length === 0) {
      return 0;
    }

    return Math.round((this.stateService.savedCount() / this.enabledSections.length) * 100);
  }

  get sectionStatuses(): Record<string, SectionSaveStatus> {
    const statuses: Record<string, SectionSaveStatus> = {};

    for (const section of this.enabledSections) {
      statuses[section.id] = this.stateService.stateFor(section.id).status;
    }

    return statuses;
  }

  get isSavingAnything(): boolean {
    return this.stateService.savingSectionId() !== null;
  }

  get currentSectionMessage(): string | undefined {
    return this.currentSection ? this.stateService.stateFor(this.currentSection.id).message : undefined;
  }

  /** Text for the page's aria-live region — announces save status changes. */
  get liveStatusAnnouncement(): string {
    const section = this.currentSection;

    if (!section) {
      return '';
    }

    const state = this.stateService.stateFor(section.id);

    switch (state.status) {
      case 'saving':
        return this.translate.instant('DOC_BUILDER.LIVE_SAVING', { title: this.translate.instant(sectionTitleKey(section.title)) });
      case 'saved':
        return this.translate.instant('DOC_BUILDER.LIVE_SAVED', { title: this.translate.instant(sectionTitleKey(section.title)) });
      case 'validation-error':
        return this.translate.instant('DOC_BUILDER.LIVE_ERROR', {
          title: this.translate.instant(sectionTitleKey(section.title)),
          message: this.translate.instant(
            state.message || 'DOC_BUILDER.FIX_HIGHLIGHTED_BEFORE_SAVING',
          ),
        });
      case 'api-error':
        return this.translate.instant('DOC_BUILDER.LIVE_ERROR', {
          title: this.translate.instant(sectionTitleKey(section.title)),
          message: this.translate.instant(state.message || 'DOC_BUILDER.SAVE_FAILED'),
        });
      default:
        return '';
    }
  }

  onActiveTabChange(index: number): void {
    const target = this.enabledSections[index];

    if (target) {
      void this.selectSection(target.id);
    }
  }

  async selectSection(targetSectionId: string): Promise<void> {
    if (targetSectionId === this.activeSectionId) {
      return;
    }

    if (!this.enabledSections.some((section) => section.id === targetSectionId)) {
      return;
    }

    const proceed = await this.confirmLeavingCurrentSectionIfDirty();

    if (proceed) {
      this.goToSectionViaUrl(targetSectionId, { replace: false });
    }
  }

  async goToPreviousSection(): Promise<void> {
    if (!this.canGoBack) {
      return;
    }

    const target = this.previousSection;

    // View mode never has unsaved changes to lose, so switch tabs directly —
    // no confirm dialog, no save call.
    const proceed = this.isEditMode ? await this.confirmLeavingCurrentSectionIfDirty() : true;

    if (proceed && target) {
      this.goToSectionViaUrl(target.id, { replace: false });
    }
  }

  async goToNextSection(): Promise<void> {
    if (!this.canGoNext) {
      return;
    }

    const target = this.nextSection;

    // View mode never has unsaved changes to lose, so switch tabs directly —
    // no confirm dialog, no save call.
    const proceed = this.isEditMode ? await this.confirmLeavingCurrentSectionIfDirty() : true;

    if (proceed && target) {
      this.goToSectionViaUrl(target.id, { replace: false });
    }
  }

  async saveCurrentSection(): Promise<void> {
    const section = this.currentSection;

    if (!section || this.isSavingAnything) {
      return;
    }

    await this.saveSection(section.id);
  }

  async saveAndGoNext():
  Promise<void> {
  const currentSection =
    this.currentSection;

  if (
    !currentSection ||
    !this.canGoNext ||
    this.isSavingAnything
  ) {
    return;
  }

  /*
   * Store the target before saving.
   * This avoids recalculating it after
   * child data reloads or state changes.
   */
  const targetSection =
    this.nextSection;

  if (!targetSection) {
    return;
  }

  this.creditService.isSaveAndContinue = true;

  let result: SectionSaveResult;

  try {
    result =
      await this.saveSection(
        currentSection.id
      );
  } finally {
    this.creditService.isSaveAndContinue = false;
  }

  if (
    result.status !== 'saved'
  ) {
    console.warn(
      'Navigation stopped because section save was not successful:',
      result
    );

    return;
  }

  this.goToSectionViaUrl(
    targetSection.id,
    {
      replace: false,
    }
  );
}

  openSubmitCreditProposalPopup(): void {
    this.submitProposalComment = '';
    this.showSubmitProposalPopup = true;
  }

  closeSubmitCreditProposalPopup(): void {
    if (this.isSubmittingCreditProposal) {
      return;
    }

    this.showSubmitProposalPopup = false;
  }

  confirmSubmitCreditProposal(): void {
    const proposalSequence = this.resolveProposalSequence();

    if (proposalSequence <= 0) {
      this.toastr.warning(this.translate.instant('DOC_BUILDER.SEQUENCE_NOT_AVAILABLE'));
      return;
    }

    const comment = this.submitProposalComment.trim();

    if (!comment) {
      this.toastr.warning(this.translate.instant('DOC_BUILDER.ENTER_SUBMISSION_COMMENT'));
      return;
    }

    if (this.isSubmittingCreditProposal) {
      return;
    }

    const payload = {
      entySeq: proposalSequence,
      entyType: this.creditProposalEntityType,
      cmmnt: comment,
    };

    this.isSubmittingCreditProposal = true;

    this.creditService.submitCreditProposal(payload).subscribe({
      next: (response: any) => {
        this.isSubmittingCreditProposal = false;

        // The global ErrorInterceptor already shows a toast for a 200
        // response whose body contains an `error` field, and tags it
        // with `__hasError` — don't show a second toast here.
        if (response?.error || response?.__hasError) {
          console.error('Credit proposal submission failed:', response);
          return;
        }

        this.toastr.success(this.translate.instant('DOC_BUILDER.PROPOSAL_SUBMITTED'));

        this.showSubmitProposalPopup = false;

        // stsKey 2502 = submitted for approval — send the user back to the
        // Credit list instead of the document preview.
        if (Number(response?.stsKey) === 2502) {
          this.isEditMode = false;
          this.router.navigate(['/cradit-list']);
          return;
        }

        this.viewReport();
      },
      error: (error: any) => {
        console.error('Credit proposal submission failed:', error);

        this.isSubmittingCreditProposal = false;

        const message =
          error?.error?.message ||
          error?.error?.error ||
          this.translate.instant('DOC_BUILDER.UNABLE_TO_SUBMIT');

        this.toastr.error(message);
      },
    });
  }

  private loadInstitutionName(): void {
    if (this.onboardingId <= 0) {
      return;
    }

    // onboardingId is a crptOnbrdngSeq — getCorporateById expects a client seq.
    this.onboardingService.getCorporateInformationByEntitySeq(this.onboardingId).subscribe({
      next: (res: any) => {
        const responseData = res?.data ?? res;
        const data = Array.isArray(responseData) ? responseData[0] : responseData;
        this.institutionName = data?.nmOfInstitution ?? '';
      },
      error: () => {
        this.institutionName = '';
      },
    });
  }

  viewReport(): void {
    if (this.reportNavigationInFlight) {
      return;
    }

    const proposalSequence = this.resolveProposalSequence();

    if (proposalSequence <= 0) {
      this.proposalSequenceError = 'DOC_BUILDER.SEQUENCE_NOT_AVAILABLE_DOT';
      return;
    }

    this.reportNavigationInFlight = true;

    this.router
      .navigate(['/report-cradit-perposal', proposalSequence], {
        queryParams: { institutionName: this.institutionName },
      })
      .finally(() => {
        this.reportNavigationInFlight = false;
      });
  }

  /** Fired by the (input)/(change)/(click) listeners around the section content. */
  onSectionContentActivity(): void {
    if (this.isEditMode && this.currentSection && !this.isDocumentsSection) {
      this.stateService.markDirty(this.currentSection.id);
    }
  }

  resolveUnsavedDialog(choice: UnsavedChangesChoice): void {
    this.showUnsavedDialog = false;
    this.unsavedDialogResolver?.(choice);
    this.unsavedDialogResolver = null;
  }

  /** Invoked by the CanDeactivate guard when navigating away from this route entirely. */
  canDeactivate(): boolean | Promise<boolean> {
    return this.confirmLeavingCurrentSectionIfDirty();
  }

  private async confirmLeavingCurrentSectionIfDirty(): Promise<boolean> {
    const currentId = this.activeSectionId;

    if (!currentId || !this.stateService.isDirty(currentId)) {
      return true;
    }

    const choice = await this.openUnsavedChangesDialog();

    if (choice === 'stay') {
      return false;
    }

    if (choice === 'discard') {
      return true;
    }

    const result = await this.saveSection(currentId);
    return result.status === 'saved';
  }

  private openUnsavedChangesDialog(): Promise<UnsavedChangesChoice> {
    this.showUnsavedDialog = true;

    return new Promise((resolve) => {
      this.unsavedDialogResolver = resolve;
    });
  }

private async saveSection(
  sectionId: string
): Promise<SectionSaveResult> {
  const proposalSequence =
    this.resolveProposalSequence();

  if (proposalSequence <= 0) {
    const validationResult:
      SectionSaveResult = {
      sectionId,
      status: 'validation-error',
      message:
        'DOC_BUILDER.SEQUENCE_REQUIRED_BEFORE_SAVING',
    };

    this.creditService
      .reportSectionSaveResult(
        validationResult
      );

    return validationResult;
  }

  if (this.isSavingAnything) {
    return {
      sectionId,
      status: 'api-error',
      message:
        'DOC_BUILDER.ANOTHER_SECTION_SAVING',
    };
  }

  this.crdtPrpslSeq =
    proposalSequence;

  this.creditService
    .setCrdtPrpslSeq(
      proposalSequence
    );

  /*
   * IMPORTANT:
   * Listener must be registered before
   * triggerSave() is called.
   */
  const saveResultPromise =
    firstValueFrom(
      this.creditService
        .sectionSaveResult$
        .pipe(
          filter(
            (
              result:
                SectionSaveResult
            ) =>
              result.sectionId ===
                sectionId &&
              result.status !==
                'saving'
          ),

          take(1),

          timeout(30000),

          catchError((error) => {
            console.error(
              `Save confirmation timeout for section "${sectionId}":`,
              error
            );

            const fallbackResult:
              SectionSaveResult = {
              sectionId,
              status: 'api-error',
              message:
                SAVE_NOT_CONFIRMED_MESSAGE,
            };

            return of(
              fallbackResult
            );
          })
        )
    );

  /*
   * Mark saving only after the result listener
   * has been prepared.
   */
  this.stateService
    .markSaving(sectionId);

  /*
   * Child component receives this and starts
   * its save API request.
   */
  this.creditService
    .triggerSave(sectionId);

  const result =
    await saveResultPromise;

  /*
   * Report fallback errors so the state service
   * can leave the saving state.
   */
  if (
    result.status ===
      'api-error' &&
    result.message ===
      SAVE_NOT_CONFIRMED_MESSAGE
  ) {
    this.creditService
      .reportSectionSaveResult(
        result
      );
  }

  return result;
}

  private resolveProposalSequence(): number {
    return Number(this.crdtPrpslSeq ?? this.creditService.getCrdtPrpslSeq() ?? 0);
  }

  /**
   * Re-derives `showSubmitCreditProposal` from what is actually persisted
   * for the given proposal — never carries over the previous proposal's
   * (or an unrelated proposal's) saved state.
   */
  private syncSubmitCreditProposalVisibility(sequence: number | null): void {
    this.showSubmitCreditProposal =
      !!sequence && this.creditService.isRepaymentPlanCalculated(sequence);
  }

  private prepareEnabledSections(): void {
    this.enabledSections = [...this.template.sections]
      .filter((section: Section) => section.enabled)
      .sort(
        (first: Section, second: Section) => Number(first.order ?? 0) - Number(second.order ?? 0),
      );
  }

  private syncActiveSectionFromUrl(requestedSectionId: string | null): void {
    const matchedSection = this.enabledSections.find(
      (section) => section.id === requestedSectionId,
    );

    if (matchedSection) {
      if (this.activeSectionId !== matchedSection.id) {
        this.activeSectionId = matchedSection.id;
        this.scrollToSectionTop();
      }

      return;
    }

    // Missing or invalid section id — fall back to the first enabled
    // section and correct the URL without adding a history entry.
    const fallbackSection = this.enabledSections[0] ?? null;
    this.activeSectionId = fallbackSection?.id ?? null;

    if (fallbackSection) {
      this.goToSectionViaUrl(fallbackSection.id, { replace: true });
    }
  }

  private goToSectionViaUrl(sectionId: string, options: { replace: boolean }): void {
    this.activeSectionId = sectionId;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { section: sectionId },
      queryParamsHandling: 'merge',
      replaceUrl: options.replace,
    });

    if (!options.replace) {
      this.scrollToSectionTop();
    }
  }

  private initializeCreditProposalSequence(): void {
    const querySequence = Number(this.route.snapshot.queryParamMap.get('crdtPrpslSeq') ?? 0);

    const stateSequence = Number(history.state?.crdtPrpslSeq ?? 0);

    const serviceSequence = Number(this.creditService.getCrdtPrpslSeq() ?? 0);

    const resolvedSequence =
      querySequence > 0
        ? querySequence
        : stateSequence > 0
          ? stateSequence
          : serviceSequence > 0
            ? serviceSequence
            : 0;

    if (resolvedSequence <= 0) {
      this.crdtPrpslSeq = null;
      this.proposalSequenceError =
        'DOC_BUILDER.SEQUENCE_NOT_PROVIDED';
      return;
    }

    this.crdtPrpslSeq = resolvedSequence;
    this.proposalSequenceError = '';

    this.creditService.setCrdtPrpslSeq(resolvedSequence);
  }

  private getRouteSequence(): number {
    let currentRoute: ActivatedRoute | null = this.route;

    while (currentRoute) {
      const routeValue =
        currentRoute.snapshot.paramMap.get('id') ?? currentRoute.snapshot.paramMap.get('oid');

      const sequence = Number(routeValue);

      if (routeValue && Number.isFinite(sequence) && sequence > 0) {
        return sequence;
      }

      currentRoute = currentRoute.parent;
    }

    return 0;
  }

  private scrollToSectionTop(): void {
    setTimeout(() => {
      const behavior: ScrollBehavior = this.prefersReducedMotion ? 'auto' : 'smooth';
      const sectionElement = document.getElementById('document-section-content');

      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior, block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior });
      }

      document.getElementById('current-section-heading')?.focus();
    });
  }

  private get prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
    );
  }
}
